// src/pages/ProductLabelsPage.js
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import productLabelService from '../services/productLabelService';
import customerService from '../services/customerService';
import SearchableSelect from '../components/SearchableSelect';
import { usePermissions } from '../context/PermissionContext';

const API_URL = process.env.REACT_APP_API_URL;

const EXP_LABEL_OPTIONS = [
  { value: 'best_by',          label: 'Best By' },
  { value: 'use_by',           label: 'Use By' },
  { value: 'exp',              label: 'Exp.' },
  { value: 'best_if_used_by',  label: 'Best If Used By' },
  { value: 'custom',           label: 'Custom…' },
];

function authHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

// ─── Delete Confirmation Modal ────────────────────────────────────────────────

function DeleteConfirmModal({ show, labelName, onCancel, onConfirm, deleting }) {
  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title text-danger">Delete Label</h5>
            </div>
            <div className="modal-body pt-2">
              <p className="mb-1">Are you sure you want to delete:</p>
              <p className="fw-semibold mb-3" style={{ fontSize: 15 }}>"{labelName}"</p>
              <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 13 }}>
                This will permanently delete the label and all its draft versions. This action cannot be undone.
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={deleting}>
                Cancel
              </button>
              <button className="btn btn-danger btn-sm" onClick={onConfirm} disabled={deleting}>
                {deleting
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Deleting…</>
                  : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── New Label Modal ───────────────────────────────────────────────────────────

function NewLabelModal({ show, onClose, onCreated, defaultLabelType, fromTab, existingTemplates = [] }) {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingDesigns, setLoadingDesigns] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState('');
  const [labelName, setLabelName] = useState('');
  const [baseVariantName, setBaseVariantName] = useState('');
  const [labelType, setLabelType] = useState(defaultLabelType || 'product');
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const [expirationLabel, setExpirationLabel] = useState('best_by');
  const [customExpLabel, setCustomExpLabel] = useState('');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [customerId, setCustomerId] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // All active labels of this type already on this product, regardless of customer — used to
  // require a customer link beyond the first one (see backend) and to steer toward Presentations
  // instead of a redundant template.
  const existingForProduct = selectedProductId
    ? existingTemplates.filter(t =>
        String(t.productId) === String(selectedProductId) &&
        (t.labelType || 'product') === labelType &&
        t.status !== 'archived'
      )
    : [];
  const customerRequired = existingForProduct.length > 0;

  // Detect if an active (non-archived) label of this type AND customer already exists —
  // a generic template and a customer-specific template for the same product can coexist.
  const conflictingTemplate = selectedProductId
    ? existingForProduct.find(t => String(t.customerId || '') === String(customerId || ''))
    : null;

  // Load products, designs, and customers when modal opens
  useEffect(() => {
    if (!show) return;
    setLabelType(defaultLabelType || 'product');

    setLoadingProducts(true);
    axios.get(`${API_URL}/api/products`, authHeaders())
      .then(r => setProducts(r.data.products || r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));

    setLoadingDesigns(true);
    productLabelService.getDesigns()
      .then(setDesigns)
      .catch(() => setDesigns([]))
      .finally(() => setLoadingDesigns(false));

    customerService.getCustomers().then(setCustomers).catch(() => setCustomers([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Auto-fill label name from product selection
  useEffect(() => {
    if (selectedProductId) {
      const prod = products.find(p => String(p.id) === String(selectedProductId));
      if (prod) setLabelName(prod.name || '');
    }
  }, [selectedProductId, products]);

  const handleClose = () => {
    setSelectedProductId('');
    setLabelName('');
    setBaseVariantName('');
    setSelectedDesignId('');
    setExpirationLabel('best_by');
    setCustomExpLabel('');
    setDateFormat('MM/DD/YYYY');
    setCustomerId('');
    setError('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductId) { setError('Please select a product.'); return; }
    if (!labelName.trim()) { setError('Please enter a label name.'); return; }
    if (customerRequired && !customerId) {
      setError('This product already has an active label. Select the customer this new one is for, or add a Presentation to the existing label instead.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const effectiveExpLabel = expirationLabel === 'custom' ? customExpLabel : expirationLabel;

      const template = await productLabelService.createTemplate({
        productId: selectedProductId,
        name: labelName.trim(),
        baseVariantName: baseVariantName.trim() || null,
        expirationLabel: effectiveExpLabel,
        dateFormat,
        labelType,
        customerId: customerId || null,
      });

      if (selectedDesignId) {
        const design = designs.find(d => String(d.id) === String(selectedDesignId));
        if (design) {
          const layout = design.rows || {};
          const snapshot = {
            width: parseFloat(design.width) || 6,
            height: parseFloat(design.height) || 4,
            ...layout,
          };
          await productLabelService.createVersion(template.id, { snapshot });
        }
      }

      onCreated(template);
      handleClose();
      navigate(`/labels/${template.id}`, { state: { fromTab: fromTab || defaultLabelType || 'product' } });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create label. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={handleClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New Label</h5>
              <button type="button" className="btn-close" onClick={handleClose} disabled={saving} />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>
                )}
                {conflictingTemplate && (
                  <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                    <strong>Duplicate label.</strong> An active {conflictingTemplate.labelType} label
                    {customerId ? ' for this customer' : ' (generic)'} already exists for this product:{' '}
                    <strong>"{conflictingTemplate.name}"</strong>.{' '}
                    <a href={`/labels/${conflictingTemplate.id}`} className="alert-link">Open it</a> to add a new version,
                    or archive it first before creating a replacement.
                  </div>
                )}

                {/* Label Type */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Label Type</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${labelType === 'product' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setLabelType('product')}
                    >
                      Product Label
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${labelType === 'case' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setLabelType('case')}
                    >
                      Case Label
                    </button>
                  </div>
                  <div className="form-text" style={{ fontSize: 12 }}>
                    {labelType === 'product'
                      ? 'Individual product labels with UPC-A (12-digit) barcode.'
                      : 'Master case labels with ITF-14 (14-digit) case barcode.'}
                  </div>
                </div>

                {/* Product */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Product</label>
                  {loadingProducts ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>Loading products…</div>
                  ) : (
                    <SearchableSelect
                      options={products.map(p => ({ value: p.id, label: p.sku ? `${p.name} (${p.sku})` : p.name }))}
                      value={selectedProductId}
                      onChange={setSelectedProductId}
                      placeholder="— Select a product —"
                      size="sm"
                    />
                  )}
                </div>

                {existingForProduct.length > 0 && (
                  <div className="alert alert-info py-2" style={{ fontSize: 13 }}>
                    This product already has {existingForProduct.length === 1 ? 'an active label' : `${existingForProduct.length} active labels`} of this type:
                    <ul className="mb-1 mt-1" style={{ paddingLeft: 18 }}>
                      {existingForProduct.map(t => (
                        <li key={t.id}>
                          <a href={`/labels/${t.id}`} className="alert-link">"{t.name}"</a>
                          {t.Customer?.name ? ` — ${t.Customer.name}` : ' — generic'}
                        </li>
                      ))}
                    </ul>
                    Need a different size or packaging of one of these? Open it and add a
                    Presentation instead of a new label. Creating a new one here requires
                    selecting the specific customer it's for, below.
                  </div>
                )}

                {/* Customer */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Customer{' '}
                    {customerRequired
                      ? <span className="text-danger fw-normal">(required)</span>
                      : <span className="text-muted fw-normal">(optional)</span>}
                  </label>
                  <SearchableSelect
                    options={customers.map(c => ({ value: c.id, label: c.name }))}
                    value={customerId}
                    onChange={setCustomerId}
                    placeholder={customerRequired ? 'Select the customer this label is for' : 'Generic — any customer'}
                    size="sm"
                  />
                  <div className="form-text" style={{ fontSize: 11 }}>
                    {customerRequired
                      ? "Required because this product already has an active label — a second one must be tied to the specific customer that requires it."
                      : "Leave blank for the default label. Set this when a specific customer requires their own design (e.g. a buyer's own GS1-128 case label)."}
                  </div>
                </div>

                {/* Label Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Label Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={labelName}
                    onChange={e => setLabelName(e.target.value)}
                    placeholder="e.g. Pepperoni Pizza Case Label"
                    required
                  />
                </div>

                {/* Base Presentation Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Base Presentation Name{' '}
                    <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={baseVariantName}
                    onChange={e => setBaseVariantName(e.target.value)}
                    placeholder="e.g. Retail 10ct, Food Service 30ct"
                  />
                  <div className="form-text" style={{ fontSize: 11 }}>
                    Name shown in the presentation picker when no variant override is selected.
                    Defaults to "Base Label" if left blank.
                  </div>
                </div>

                {/* Design */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Label Design{' '}
                    <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  {loadingDesigns ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>Loading designs…</div>
                  ) : (
                    <select
                      className="form-select form-select-sm"
                      value={selectedDesignId}
                      onChange={e => setSelectedDesignId(e.target.value)}
                    >
                      <option value="">No design (blank)</option>
                      {designs.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name} ({d.width || '?'}" × {d.height || '?'}")
                        </option>
                      ))}
                    </select>
                  )}
                  {designs.length === 0 && !loadingDesigns && (
                    <div className="form-text">
                      No designs yet.{' '}
                      <Link to="/label-designs" onClick={handleClose}>Create one in Label Designs.</Link>
                    </div>
                  )}
                </div>

                {/* Expiration Label */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Expiration Label</label>
                  <select
                    className="form-select form-select-sm"
                    value={expirationLabel}
                    onChange={e => setExpirationLabel(e.target.value)}
                  >
                    {EXP_LABEL_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {expirationLabel === 'custom' && (
                    <input
                      type="text"
                      className="form-control form-control-sm mt-2"
                      placeholder="Enter custom expiration label text"
                      value={customExpLabel}
                      onChange={e => setCustomExpLabel(e.target.value)}
                    />
                  )}
                </div>

                {/* Date Format */}
                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Date Format</label>
                  <select
                    className="form-select form-select-sm"
                    value={dateFormat}
                    onChange={e => setDateFormat(e.target.value)}
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 12/31/2025)</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 31/12/2025)</option>
                    <option value="DD-MMM-YYYY">DD-MMM-YYYY (e.g. 31-Dec-2025)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2025-12-31)</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !!conflictingTemplate || (customerRequired && !customerId)}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" />
                      Creating…
                    </>
                  ) : 'Create Label'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Quick Print — product-first entry point ───────────────────────────────────
// Floor staff often don't know (or shouldn't have to know) which of several label
// designs applies to what they're producing. This lets them pick the product and the
// order it's for, then routes straight into that order's correct label — resolved
// server-side from the order's customer, falling back to the generic label. See the
// "Multiple Label Designs per Product" section of docs/PRODUCT_LABELS_MODULE.md.
function QuickPrintModal({ show, onClose, defaultLabelType }) {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productId, setProductId] = useState('');
  const [labelType, setLabelType] = useState(defaultLabelType || 'case');

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderId, setOrderId] = useState('');

  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setLabelType(defaultLabelType || 'case');
    setLoadingProducts(true);
    axios.get(`${API_URL}/api/products`, authHeaders())
      .then(r => setProducts(r.data.products || r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    setOrderId('');
    setOrders([]);
    if (!productId) return;
    setLoadingOrders(true);
    productLabelService.getOpenOrders(productId)
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));
  }, [productId]);

  const handleClose = () => {
    setProductId(''); setOrderId(''); setOrders([]); setError('');
    onClose();
  };

  const handleGo = async () => {
    if (!productId) { setError('Please select a product.'); return; }
    setResolving(true);
    setError('');
    try {
      const selectedOrder = orders.find(o => String(o.id) === String(orderId)) || null;
      const { templateId } = await productLabelService.resolveTemplate(
        productId, labelType, selectedOrder?.customerId
      );
      handleClose();
      navigate(`/labels/${templateId}`, {
        state: {
          fromTab: labelType,
          autoOpenPrint: true,
          prefillOrderId: selectedOrder?.id || null,
        },
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'No active label found for this product.');
    } finally {
      setResolving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={handleClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Quick Print</h5>
              <button type="button" className="btn-close" onClick={handleClose} disabled={resolving} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Label Type</label>
                <div className="btn-group w-100" role="group">
                  <button type="button" className={`btn btn-sm ${labelType === 'product' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setLabelType('product')}>
                    Product Label
                  </button>
                  <button type="button" className={`btn btn-sm ${labelType === 'case' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setLabelType('case')}>
                    Case Label
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Product</label>
                {loadingProducts ? (
                  <div className="text-muted" style={{ fontSize: 13 }}>Loading products…</div>
                ) : (
                  <SearchableSelect
                    options={products.map(p => ({ value: p.id, label: p.sku ? `${p.name} (${p.sku})` : p.name }))}
                    value={productId}
                    onChange={setProductId}
                    placeholder="— Select a product —"
                    size="sm"
                  />
                )}
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                  Order / Customer{' '}
                  <span className="text-muted fw-normal">(optional)</span>
                </label>
                {loadingOrders ? (
                  <div className="text-muted" style={{ fontSize: 13 }}>Loading open orders…</div>
                ) : (
                  <SearchableSelect
                    options={orders.map(o => ({
                      value: o.id,
                      label: `${o.customerName || 'No customer'} — PO ${o.poNumber || o.orderNumber}`,
                    }))}
                    value={orderId}
                    onChange={setOrderId}
                    placeholder={productId ? 'No order — use generic label' : 'Select a product first'}
                    disabled={!productId}
                    size="sm"
                  />
                )}
                <div className="form-text" style={{ fontSize: 11 }}>
                  If this order's customer has their own label design, it's used automatically;
                  otherwise the generic label prints.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClose} disabled={resolving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleGo} disabled={resolving || !productId}>
                {resolving ? (
                  <><span className="spinner-border spinner-border-sm me-1" role="status" />Loading…</>
                ) : 'Continue to Print'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function PrintHistoryTab() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Filters
  const [filterProduct, setFilterProduct] = useState('');
  const [filterLotCode, setFilterLotCode] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    productLabelService.getTemplates()
      .then(tpls => {
        const seen = new Map();
        tpls.forEach(t => { if (t.Product && !seen.has(t.Product.id)) seen.set(t.Product.id, t.Product); });
        setProducts([...seen.values()].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {});
  }, []);

  const doFetch = (pageNum, product, lotCode, dateFrom, dateTo) => {
    setLoading(true);
    productLabelService.getPrintHistory({
      ...(product && { productId: product }),
      ...(lotCode.trim() && { lotCode: lotCode.trim() }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
      limit: PAGE_SIZE,
      offset: pageNum * PAGE_SIZE,
    })
      .then(({ jobs: j, total: t }) => { setJobs(j); setTotal(t); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetch = (reset = false) => {
    const nextPage = reset ? 0 : page;
    if (reset) setPage(0);
    doFetch(nextPage, filterProduct, filterLotCode, filterDateFrom, filterDateTo);
  };

  useEffect(() => { doFetch(0, '', '', '', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString() : '—';

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Filters */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
        <div>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>Product</label>
          <select className="form-select form-select-sm" style={{ minWidth: 160 }}
            value={filterProduct} onChange={e => setFilterProduct(e.target.value)}>
            <option value="">All products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>Lot Code</label>
          <input className="form-control form-control-sm" style={{ minWidth: 130 }}
            placeholder="Search lot…" value={filterLotCode}
            onChange={e => setFilterLotCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetch(true)} />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>From</label>
          <input type="date" className="form-control form-control-sm"
            value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>To</label>
          <input type="date" className="form-control form-control-sm"
            value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => fetch(true)} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Search'}
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => {
          setFilterProduct(''); setFilterLotCode(''); setFilterDateFrom(''); setFilterDateTo('');
          setPage(0); doFetch(0, '', '', '', '');
        }}>Clear</button>
        <span className="text-muted ms-auto" style={{ fontSize: 12 }}>{total} record{total !== 1 ? 's' : ''}</span>
      </div>

      {loading && jobs.length === 0 ? (
        <div className="text-center text-muted py-5">
          <span className="spinner-border spinner-border-sm me-2" role="status" />Loading…
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center text-muted py-5" style={{ fontSize: 13 }}>No print jobs found.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: 13 }}>
              <thead className="table-light">
                <tr>
                  <th>Printed At</th>
                  <th>Product</th>
                  <th>Lot Code</th>
                  <th>PO / Order</th>
                  <th>Prod. Date</th>
                  <th>Exp. Date</th>
                  <th>Presentation</th>
                  <th>Version</th>
                  <th>Printed By</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(j.printedAt)}</td>
                    <td>{j.Product?.name || '—'}</td>
                    <td><code style={{ fontSize: 12 }}>{j.lotCode || '—'}</code></td>
                    <td>
                      {j.poNumber || '—'}
                      {j.CustomerOrder && (
                        <span className="ms-1" title={`Linked to order ${j.CustomerOrder.orderNumber}`}>🔗</span>
                      )}
                    </td>
                    <td>{fmtDate(j.productionDate)}</td>
                    <td>{fmtDate(j.expirationDate)}</td>
                    <td>{j.Variant ? j.Variant.name : <span className="text-muted">Base</span>}</td>
                    <td>{j.LabelVersion ? `v${j.LabelVersion.versionNumber}` : '—'}</td>
                    <td>{j.PrintedBy?.username || '—'}</td>
                    <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={j.notes || ''}>{j.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: 13 }}>
              <button className="btn btn-outline-secondary btn-sm" disabled={page === 0}
                onClick={() => { const p = page - 1; setPage(p); doFetch(p, filterProduct, filterLotCode, filterDateFrom, filterDateTo); }}>← Prev</button>
              <span className="text-muted">Page {page + 1} of {totalPages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages - 1}
                onClick={() => { const p = page + 1; setPage(p); doFetch(p, filterProduct, filterLotCode, filterDateFrom, filterDateTo); }}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProductLabelsPage() {
  const { hasModule } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const tabFromUrl = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(['product', 'case', 'print_history'].includes(tabFromUrl) ? tabFromUrl : 'product'); // 'product' | 'case' | 'print_history'
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showQuickPrintModal, setShowQuickPrintModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [archivingId, setArchivingId] = useState(null);

  const load = () => {
    productLabelService.getTemplates()
      .then(setTemplates)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const pendingCount = templates.reduce((n, t) =>
    n + (t.Versions || []).filter(v => v.status === 'pending_approval').length, 0);

  const handleLabelCreated = (_template) => { load(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await productLabelService.deleteTemplate(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete label.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleArchive = async (t) => {
    setArchivingId(t.id);
    try {
      await productLabelService.updateTemplate(t.id, { status: 'archived' });
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to archive label.');
    } finally {
      setArchivingId(null);
    }
  };

  const handleRestore = async (t) => {
    setArchivingId(t.id);
    try {
      await productLabelService.updateTemplate(t.id, { status: 'active' });
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to restore label.');
    } finally {
      setArchivingId(null);
    }
  };

  // Filter by tab + archived toggle + free-text search (label name, product name, customer name)
  const searchNeedle = searchQuery.trim().toLowerCase();
  const visibleTemplates = templates.filter(t => {
    const matchesType = (t.labelType || 'product') === activeTab;
    const matchesArchived = showArchived ? t.status === 'archived' : t.status !== 'archived';
    const matchesSearch = !searchNeedle || [t.name, t.Product?.name, t.Customer?.name]
      .some(v => v?.toLowerCase().includes(searchNeedle));
    return matchesType && matchesArchived && matchesSearch;
  });

  const productCount = templates.filter(t => (t.labelType || 'product') === 'product' && t.status !== 'archived').length;
  const caseCount = templates.filter(t => (t.labelType || 'product') === 'case' && t.status !== 'archived').length;

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h4 className="mb-0">Product Labels</h4>
          {pendingCount > 0 && (
            <span className="badge bg-warning text-dark">
              {pendingCount} pending approval
            </span>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {hasModule('labels.print') && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowQuickPrintModal(true)}>
              🖨 Quick Print
            </button>
          )}
          {hasModule('labels.edit') && (
            <Link to="/label-designs" className="btn btn-outline-secondary btn-sm">
              Label Designs
            </Link>
          )}
          {hasModule('labels.edit') && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowNewModal(true)}>
              + New Label
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-0" style={{ borderBottom: 'none' }}>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'product' ? 'active' : ''}`}
            onClick={() => { setActiveTab('product'); setShowArchived(false); }}
          >
            Product Labels
            {productCount > 0 && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{productCount}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'case' ? 'active' : ''}`}
            onClick={() => { setActiveTab('case'); setShowArchived(false); }}
          >
            Case Labels
            {caseCount > 0 && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: 11 }}>{caseCount}</span>
            )}
          </button>
        </li>
        {hasModule('labels.print_history') && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'print_history' ? 'active' : ''}`}
              onClick={() => { setActiveTab('print_history'); setShowArchived(false); }}
            >
              Print History
            </button>
          </li>
        )}
      </ul>

      <div className="border border-top-0 rounded-bottom p-3" style={{ background: '#fff' }}>
        {activeTab === 'print_history' ? (
          <PrintHistoryTab />
        ) : (
        <>
        {/* Search + Show Archived toggle */}
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 280 }}
            placeholder="Search by label, product, or customer…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="form-check form-switch" style={{ fontSize: 13 }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="showArchivedToggle"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
            />
            <label className="form-check-label text-muted" htmlFor="showArchivedToggle">
              Show archived
            </label>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-muted py-5">
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Loading…
          </div>
        ) : visibleTemplates.length === 0 ? (
          <div className="text-center text-muted py-5">
            <div style={{ fontSize: 40 }}>🏷️</div>
            {showArchived ? (
              <p className="mt-2">No archived {activeTab} labels.</p>
            ) : (
              <>
                <p className="mt-2">No {activeTab} labels yet.</p>
                {hasModule('labels.edit') && (
                  <p style={{ fontSize: 13 }}>
                    Click <strong>+ New Label</strong> to create your first {activeTab} label.
                  </p>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Product</th>
                  <th>Label Name</th>
                  <th>Active Version</th>
                  <th>Status</th>
                  <th style={{ width: 200 }}></th>
                </tr>
              </thead>
              <tbody>
                {visibleTemplates.map(t => {
                  const versions = t.Versions || [];
                  const activeVersion = versions.find(v => v.id === t.activeVersionId);
                  const pendingVersions = versions.filter(v => v.status === 'pending_approval');
                  const draftVersions = versions.filter(v => v.status === 'draft');
                  const isArchiving = archivingId === t.id;

                  return (
                    <tr key={t.id} style={t.status === 'archived' ? { opacity: 0.65 } : {}}>
                      <td>{t.Product?.name || '—'}</td>
                      <td>
                        <span className="fw-semibold">{t.name}</span>
                        {t.Customer?.name && (
                          <span className="badge bg-info text-dark ms-2" style={{ fontSize: 10 }}>
                            {t.Customer.name}
                          </span>
                        )}
                      </td>
                      <td>
                        {activeVersion ? (
                          <span className="badge bg-success">
                            v{activeVersion.versionNumber} Approved
                          </span>
                        ) : (
                          <span className="text-muted" style={{ fontSize: 12 }}>No approved version</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {t.status === 'archived' ? (
                            <span className="badge bg-secondary">Archived</span>
                          ) : pendingVersions.length > 0 ? (
                            <span className="badge bg-warning text-dark">
                              {pendingVersions.length === 1 ? 'Pending Review' : `${pendingVersions.length} Pending`}
                            </span>
                          ) : activeVersion ? (
                            <span className="badge bg-success">Approved</span>
                          ) : draftVersions.length > 0 ? (
                            <span className="badge bg-secondary">Draft</span>
                          ) : (
                            <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                          )}
                        </div>
                      </td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {t.status === 'archived' ? (
                          <>
                            {hasModule('labels.view') && (
                              <button
                                className="btn btn-outline-secondary btn-sm me-1"
                                onClick={() => navigate(`/labels/${t.id}`, { state: { fromTab: activeTab } })}
                              >
                                View
                              </button>
                            )}
                            {hasModule('labels.edit') && (
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={() => handleRestore(t)}
                                disabled={isArchiving}
                              >
                                {isArchiving ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Restore'}
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {hasModule('labels.view') && (
                              <button
                                className="btn btn-outline-secondary btn-sm me-1"
                                onClick={() => navigate(`/labels/${t.id}`, { state: { fromTab: activeTab } })}
                              >
                                View
                              </button>
                            )}
                            {hasModule('labels.print') && t.activeVersionId && (
                              <button
                                className="btn btn-outline-primary btn-sm me-1"
                                onClick={() => navigate(`/labels/${t.id}`, { state: { fromTab: activeTab } })}
                              >
                                Print
                              </button>
                            )}
                            {hasModule('labels.edit') && t.activeVersionId && (
                              <button
                                className="btn btn-outline-secondary btn-sm me-1"
                                onClick={() => handleArchive(t)}
                                disabled={isArchiving}
                                title="Archive this label"
                              >
                                {isArchiving ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Archive'}
                              </button>
                            )}
                            {hasModule('labels.edit') && !t.activeVersionId && (
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setDeleteTarget(t)}
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      <NewLabelModal
        show={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={handleLabelCreated}
        defaultLabelType={activeTab}
        fromTab={activeTab}
        existingTemplates={templates}
      />

      <QuickPrintModal
        show={showQuickPrintModal}
        onClose={() => setShowQuickPrintModal(false)}
        defaultLabelType={activeTab === 'print_history' ? 'case' : activeTab}
      />

      <DeleteConfirmModal
        show={!!deleteTarget}
        labelName={deleteTarget?.name}
        deleting={deletingId === deleteTarget?.id}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

export default ProductLabelsPage;
